import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import StatusTag from '@/components/_common/StatusTag';
import {
  workorderComplementColor,
  WorkOrderComplementStatus,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { Row, Col, Space, Typography, Button, Collapse } from 'antd';
import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { Group } from '@/components/_common';
import ComplementHeaderTag from './ComplementHeaderTag';
import ComplementHeaderStatus from './ComplementHeaderStatus';
import VehicleDetailsDescriptions from '@/components/_common/VehicleDetailsDescriptions';
import { isAppPortocar } from '@/index';
import ComplementHeaderGP from './ComplementHeaderGP';
import BoschCoRe from '../../BoschCoRe';
import ComplementStatusHistoryDrawer from '../ComplementStatusHistoryDrawer';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

interface Props {
  isGeneralData?: boolean;
}

const ComplementHeader = ({ isGeneralData = false }: Props) => {
  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();
  const history = useHistory();
  const intl = useIntl();

  const [isLastComplement, setIsLastComplement] = useState(false);
  const [isFirstComplement, setIsFirstComplement] = useState(false);

  const workorderNumber = String(workorder?.number).padStart(6, '0');
  const complementNumber = String(complement?.number).padStart(2, '0');

  const hasComplementClosed = workorder?.complements.some(
    (complement) => complement.status === WorkOrderComplementStatus.Closed,
  );
  const hasVehicle = !!workorder?.vehicle;

  useEffect(() => {
    if (workorder && complement) {
      setIsFirstComplement(getIndex() === 0);
      setIsLastComplement(getIndex() === workorder.complements.length - 1);
    }
  }, [workorder, complement]);

  const getIndex = () =>
    workorder?.complements.findIndex((item) => item.id === complement?.id) || 0;

  const tagColor = complement?.status
    ? workorderComplementColor[complement.status]
    : 'red';

  function nextComplement() {
    const nextCurrentComplement = getIndex() + 1;
    const complementData = workorder?.complements[nextCurrentComplement];

    if (complementData) {
      history.push(
        `/app/movements/workOrders/${workorder.id}/complements/${complementData.id}/generalInfo`,
      );
    }
  }

  function prevComplement() {
    const prevCurrentComplement = getIndex() - 1;
    const complementData = workorder?.complements[prevCurrentComplement];

    if (complementData) {
      history.push(
        `/app/movements/workOrders/${workorder.id}/complements/${complementData.id}/generalInfo`,
      );
    }
  }

  return (
    <>
      <Row
        gutter={[8, 12]}
        align="middle"
        style={{ marginBottom: '24px' }}
        id="workorder-detail-header"
      >
        <Col>
          <Typography.Title level={4} id="status" style={{ marginBottom: 0 }}>
            <Group gap={5}>
              <FormattedMessage id="commons.entity.situation" />:
              <ComplementStatusHistoryDrawer>
                <StatusTag
                  size="lg"
                  color={tagColor}
                  label={
                    <Group gap={5}>
                      {intl.formatMessage({
                        id: `workOrders.domain.enums.work_order_status.${
                          complement?.status.split('.').slice(-1)[0]
                        }`,
                      })}

                      {hasPermissions([
                        Authorities.MOVIMENTATION_WORKORDER_VIEW_HISTORY,
                      ]) && <Clock size={16} />}
                    </Group>
                  }
                />
              </ComplementStatusHistoryDrawer>
            </Group>
          </Typography.Title>
        </Col>

        {isAppPortocar && workorder?.isPortoBatteryWorkOrder && (
          <StatusTag label="Bateria (7051)" color="yellow" size="lg" />
        )}

        <Col>
          <Typography.Title level={4} id="status" style={{ marginBottom: 0 }}>
            <ComplementHeaderTag />
          </Typography.Title>
        </Col>

        <Col flex={1}>
          <Typography.Title level={4} id="status" style={{ marginBottom: 0 }}>
            <ComplementHeaderStatus />
          </Typography.Title>
        </Col>

        <Col>
          <BoschCoRe />
        </Col>

        <ComplementHeaderGP />

        <Col>
          <Space align="center">
            <Button
              icon={
                <AntdFeatherIcon
                  icon={<ChevronLeft cursor="pointer" />}
                  size={14}
                />
              }
              onClick={prevComplement}
              disabled={isFirstComplement}
              size="small"
            />

            <Typography.Title level={5} style={{ margin: 0 }}>
              {`${workorderNumber}/${complementNumber}`}
            </Typography.Title>

            <Button
              icon={
                <AntdFeatherIcon
                  icon={<ChevronRight cursor="pointer" />}
                  size={14}
                />
              }
              size="small"
              onClick={nextComplement}
              disabled={isLastComplement}
            />
          </Space>
        </Col>

        {!isGeneralData && !(hasComplementClosed && !hasVehicle) && (
          <Col span={24}>
            <Collapse>
              <Collapse.Panel
                header={intl.formatMessage({
                  id: 'workOrders.details.vehicleData',
                })}
                key="vehicle"
              >
                <VehicleDetailsDescriptions />
              </Collapse.Panel>
            </Collapse>
          </Col>
        )}
      </Row>
    </>
  );
};

export default ComplementHeader;
